// Service de configuration pour les appels API
class ApiService {
  constructor() {
    // URL de base de l'API - utilise la variable d'environnement ou un fallback
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3800/api';
    
    // URLs spécifiques pour différents environnements
    this.config = {
      development: 'http://localhost:3800/api',
      production: 'https://api.brievents.com/api',
      staging: 'https://api-staging.brievents.com/api'
    };

    // Déterminer l'environnement actuel
    this.environment = process.env.NODE_ENV || 'development';
    
    // Utiliser l'URL appropriée selon l'environnement
    if (this.config[this.environment]) {
      this.baseURL = this.config[this.environment];
    }

    console.log(`🔗 API Service initialized with base URL: ${this.baseURL}`);
  }

  // Méthode pour obtenir l'URL complète d'un endpoint
  getUrl(endpoint) {
    // Supprimer les slashes en début si présents
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return `${this.baseURL}/${cleanEndpoint}`;
  }

  // URLs spécifiques pour l'administration
  admin = {
    // Utilisateurs admin
    login: () => this.getUrl('admin/user/login'),
    verifyUser: () => this.getUrl('admin/user/verify'),
    logout: () => this.getUrl('admin/user/logout'),
    
    // Sponsors
    getSponsors: (params = {}) => {
      const { page = 1, limit = 10, sortBy = 'createdAt', order = 'desc' } = params;
      return this.getUrl(`admin/sponsor?page=${page}&limit=${limit}&sortBy=${sortBy}&order=${order}`);
    },
    getPendingSponsors: () => this.getUrl('admin/sponsor/pending/list'),
    getSponsorById: (sponsorId) => this.getUrl(`admin/sponsor/${sponsorId}`),
    getSponsorStats: (sponsorId) => this.getUrl(`admin/sponsor/${sponsorId}/stats`),
    deleteSponsor: (sponsorId) => this.getUrl(`admin/sponsor/${sponsorId}`),
    validateSponsor: (sponsorId) => this.getUrl(`admin/sponsor/pending/${sponsorId}/validate`),
    updateSponsor: (sponsorId) => this.getUrl(`admin/sponsor/${sponsorId}`),
    
    // Organisateurs
    getOrganizers: (params = {}) => {
      const { page = 1, limit = 10, sortBy = 'createdAt', order = 'desc', search = '' } = params;
      let url = `admin/organizer?page=${page}&limit=${limit}&sortBy=${sortBy}&order=${order}`;
      if (search) url += `&search=${search}`;
      return this.getUrl(url);
    },
    getPendingOrganizers: () => this.getUrl('admin/organizer/pending/list'),
    deleteOrganizer: (organizerId) => this.getUrl(`admin/organizer/${organizerId}`),
    validateOrganizer: (organizerId) => this.getUrl(`admin/organizer/pending/${organizerId}/validate`),
    updateOrganizer: (organizerId) => this.getUrl(`admin/organizer/${organizerId}`),
    
    // Événements
    getEvents: (params = {}) => {
      const { page = 1, limit = 10, sortBy = 'createdAt', order = 'desc', status = '', search = '', startDate = '', endDate = '' } = params;
      let url = `admin/event?page=${page}&limit=${limit}&sortBy=${sortBy}&order=${order}`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${search}`;
      if (startDate) url += `&startDate=${startDate}`;
      if (endDate) url += `&endDate=${endDate}`;
      return this.getUrl(url);
    },
    createEvent: () => this.getUrl('admin/event'),
    updateEventStatus: (eventId) => this.getUrl(`admin/event/${eventId}/status`),
    deleteEvent: (eventId) => this.getUrl(`admin/event/${eventId}`),
    updateEvent: (eventId) => this.getUrl(`admin/event/${eventId}`),
    
    // Prestataires
    getProviders: (params = {}) => {
      const { page = 1, limit = 10, sortBy = 'createdAt', order = 'desc', search = '', serviceCategory = '', isActive, minRating = '' } = params;
      let url = `admin/prestataire?page=${page}&limit=${limit}&sortBy=${sortBy}&order=${order}`;
      if (search) url += `&search=${search}`;
      if (serviceCategory) url += `&serviceCategory=${serviceCategory}`;
      if (isActive !== undefined) url += `&isActive=${isActive}`;
      if (minRating) url += `&minRating=${minRating}`;
      return this.getUrl(url);
    },
    getProviderStats: (providerId) => this.getUrl(`admin/prestataire/${providerId}/stats`),
    deleteProvider: (providerId) => this.getUrl(`admin/prestataire/${providerId}`),
    toggleProviderStatus: (providerId) => this.getUrl(`admin/prestataire/${providerId}/toggle-status`),
    updateProvider: (providerId) => this.getUrl(`admin/prestataire/${providerId}`),
    
    // Récompenses
    getRewardLevels: () => this.getUrl('admin/reward/levels'),
    createRewardLevel: () => this.getUrl('admin/reward/levels'),
    updateRewardLevel: (levelId) => this.getUrl(`admin/reward/levels/${levelId}`),
    deleteRewardLevel: (levelId) => this.getUrl(`admin/reward/levels/${levelId}`),
    getRewardPayments: (params = {}) => {
      const { page = 1, limit = 10, status = '', search = '' } = params;
      let url = `admin/reward/payments?page=${page}&limit=${limit}`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${search}`;
      return this.getUrl(url);
    },
    getPaymentStats: () => this.getUrl('admin/reward/payments/stats'),
    updatePaymentStatus: (paymentId) => this.getUrl(`admin/reward/payments/${paymentId}/status`)
  };

  // Méthode utilitaire pour tester la connectivité
  async testConnection() {
    try {
      const testUrl = this.getUrl('health') || this.baseURL.replace('/api', '/health');
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        console.log('✅ Connection to API successful');
        return { success: true, status: response.status };
      } else {
        console.warn('⚠️ API responded but with non-200 status:', response.status);
        return { success: false, status: response.status, error: 'Non-200 response' };
      }
    } catch (error) {
      console.error('❌ Failed to connect to API:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Méthode pour obtenir les informations de configuration
  getConfig() {
    return {
      baseURL: this.baseURL,
      environment: this.environment,
      availableEndpoints: Object.keys(this.admin)
    };
  }
}

// Créer et exporter une instance unique (singleton)
const apiService = new ApiService();

export default apiService;

// Export nommés pour une utilisation flexible
export const { admin } = apiService;
export const getApiUrl = (endpoint) => apiService.getUrl(endpoint);
export const testApiConnection = () => apiService.testConnection();
export const getApiConfig = () => apiService.getConfig();
